import { useState } from "react";
import { X, Plus } from "lucide-react";
import { Label } from "./ui/label";
import { FREQ_OPTIONS, WEEK_OF_MONTH_OPTIONS, normFreq, todayIso } from "../lib/planning";

const selectCls = "h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm";
const thisMonth = () => todayIso().slice(0, 7);
const fmtD = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

const Field = ({ label, compact, children }) => (
    <div className={compact ? "flex items-center gap-1" : ""}>
        {!compact && label && <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</Label>}
        {children}
    </div>
);

const NumberField = ({ value, min, max, unit, onChange, cls, testId }) => (
    <div className="flex items-center gap-1">
        <span className="text-xs text-slate-500">ogni</span>
        <input type="number" min={min} max={max} value={value} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            className={`${cls} w-16 text-center`} data-testid={testId} />
        <span className="text-xs text-slate-500">{unit}</span>
    </div>
);

const DatesEditor = ({ dates, onChange, cls, testPrefix }) => {
    const [draft, setDraft] = useState("");
    const add = () => {
        if (!draft || dates.includes(draft)) return;
        onChange([...dates, draft].sort());
        setDraft("");
    };
    return (
        <div className="w-full space-y-1.5" data-testid={`${testPrefix}-dates`}>
            <div className="flex items-center gap-1">
                <input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} className={cls} data-testid={`${testPrefix}-date-input`}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
                <button type="button" onClick={add} disabled={!draft}
                    className="flex h-8 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-[#376DB3] hover:bg-slate-50 disabled:opacity-40"
                    data-testid={`${testPrefix}-date-add`}>
                    <Plus className="h-3 w-3" /> Aggiungi
                </button>
            </div>
            <div className="flex flex-wrap gap-1">
                {dates.length === 0 && <span className="text-[11px] text-slate-400">Nessuna data: aggiungi le date degli interventi</span>}
                {dates.map((d) => (
                    <span key={d} className="inline-flex items-center gap-1 rounded-full bg-[#376DB3]/10 px-2 py-0.5 font-mono-numbers text-[11px] font-semibold text-[#376DB3]" data-testid={`${testPrefix}-date-${d}`}>
                        {fmtD(d)}
                        <button type="button" onClick={() => onChange(dates.filter((x) => x !== d))} className="text-[#376DB3]/60 hover:text-red-500" data-testid={`${testPrefix}-date-remove-${d}`}>
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );
};

export function FrequencyPicker({ value, onChange, testPrefix = "freq", compact = false }) {
    const f = normFreq(value);
    const type = f.type || "weekly";
    const set = (patch) => onChange({ ...f, ...patch });
    const setType = (t) => {
        if (t === "every_n_weeks") onChange({ type: t, n: 2, anchor: f.anchor || todayIso() });
        else if (t === "monthly") onChange({ type: t, week_of_month: f.week_of_month ?? 1 });
        else if (t === "every_n_months") onChange({ type: t, n: 3, week_of_month: f.week_of_month ?? 1, anchor_month: thisMonth() });
        else if (t === "every_n_days") onChange({ type: t, n: 10, anchor: f.anchor || todayIso() });
        else if (t === "dates") onChange({ type: t, dates: f.dates || [] });
        else onChange({ type: "weekly" });
    };
    const inputCls = compact ? "h-8 rounded-md border border-slate-200 bg-white px-1.5 text-xs" : selectCls;
    const womSelect = (
        <select value={String(f.week_of_month ?? 1)} onChange={(e) => set({ week_of_month: Number(e.target.value) })} className={inputCls} data-testid={`${testPrefix}-wom`}>
            {WEEK_OF_MONTH_OPTIONS.map((o) => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
        </select>
    );
    return (
        <div className={compact ? "flex flex-wrap items-center gap-1.5" : "grid gap-2 sm:grid-cols-2"}>
            <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid={`${testPrefix}-type`}>
                {FREQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {type === "every_n_weeks" && (
                <>
                    <Field compact={compact} label="Ogni quante settimane">
                        <NumberField value={f.n ?? 2} min={2} max={52} unit="settimane" onChange={(n) => set({ n })} cls={inputCls} testId={`${testPrefix}-n`} />
                    </Field>
                    <Field compact={compact} label="Settimana in cui si lavora">
                        <input type="date" value={f.anchor || ""} onChange={(e) => set({ anchor: e.target.value })} className={inputCls}
                            title="Una data qualsiasi di una settimana in cui l'intervento viene svolto" data-testid={`${testPrefix}-anchor`} />
                    </Field>
                </>
            )}
            {type === "monthly" && womSelect}
            {type === "every_n_months" && (
                <>
                    <Field compact={compact} label="Ogni quanti mesi">
                        <NumberField value={f.n ?? 3} min={2} max={12} unit="mesi" onChange={(n) => set({ n })} cls={inputCls} testId={`${testPrefix}-n`} />
                    </Field>
                    <Field compact={compact} label="Settimana del mese">{womSelect}</Field>
                    <Field compact={compact} label="Mese di partenza">
                        <input type="month" value={f.anchor_month || ""} onChange={(e) => set({ anchor_month: e.target.value })} className={inputCls}
                            title="Primo mese in cui l'intervento viene svolto" data-testid={`${testPrefix}-anchor-month`} />
                    </Field>
                </>
            )}
            {type === "every_n_days" && (
                <>
                    <Field compact={compact} label="Ogni quanti giorni">
                        <NumberField value={f.n ?? 10} min={2} max={365} unit="giorni" onChange={(n) => set({ n })} cls={inputCls} testId={`${testPrefix}-n`} />
                    </Field>
                    <Field compact={compact} label="Data del primo intervento">
                        <input type="date" value={f.anchor || ""} onChange={(e) => set({ anchor: e.target.value })} className={inputCls}
                            title="Da questa data si ripete ogni N giorni" data-testid={`${testPrefix}-anchor`} />
                    </Field>
                </>
            )}
            {type === "dates" && (
                <div className={compact ? "basis-full" : "sm:col-span-2"}>
                    <DatesEditor dates={f.dates || []} onChange={(dates) => set({ dates })} cls={inputCls} testPrefix={testPrefix} />
                </div>
            )}
        </div>
    );
}
